import BANK from './bank'
import { localDateKey } from '../wordle/gameLogic'

// Re-export so the page only needs to import from one place.
export { localDateKey }

// djb2 hash of a date key — same algorithm Wordle uses. We hash the
// YYYY-MM-DD string rather than the underlying day-count so future
// changes to the bank ordering don't shift today's question relative
// to last week's. The bank order is the source of truth.
function hashDateKey(key) {
  let h = 5381
  for (let i = 0; i < key.length; i++) {
    h = ((h << 5) + h + key.charCodeAt(i)) >>> 0
  }
  return h
}

// Today's question, optionally avoiding ids the pair has already
// answered recently. The hash-derived index gives the starting
// candidate; if that question is in the exclusion set, we walk
// forward through BANK looking for the next free slot.
//
// Both partners pass the same exclusion set (derived from the shared
// couples_question_answers table via useRecentDailyQuestionIds, with
// `day < today` so today's pick stays stable while the day is in
// progress), so the deterministic-same-on-both-sides contract holds.
//
// When the entire bank is in the exclusion set (every question used
// in the cooldown window), we return the base candidate — a forced
// repeat beats throwing.
export function pickDailyQuestion(date = new Date(), exclude = null) {
  const base = hashDateKey(localDateKey(date)) % BANK.length
  if (!exclude || exclude.size === 0) return BANK[base]
  for (let offset = 0; offset < BANK.length; offset++) {
    const idx = (base + offset) % BANK.length
    if (!exclude.has(BANK[idx].id)) return BANK[idx]
  }
  return BANK[base]
}

// Cheap lookup by id. Used by the partner-row render after the reveal
// — we get back the partner's stored question_id + choice_id and need
// to render their option's text. Linear scan is fine at ~150 entries.
// `pool` lets callers pass the merged BANK + user-suggestions pool
// (see useQuestionPool) so questions stored against a sug-* id still
// resolve. Defaults to BANK so old call sites stay valid.
export function findQuestion(questionId, pool = BANK) {
  return pool.find((q) => q.id === questionId) || null
}

export function findChoice(question, choiceId) {
  if (!question) return null
  return question.options.find((o) => o.id === choiceId) || null
}

// Walk back through completed (both-answered) days to count the joint
// streak. Today contributes once we've both answered; if either side
// hasn't, the streak we surface is yesterday-back. Same shape as
// Wordle's computeStreak so the UI feels parallel.
export function computeJointStreak(myDays, partnerDays, today = new Date()) {
  if (!myDays?.size || !partnerDays?.size) return 0
  let streak = 0
  const cursor = new Date(today)
  const todayKey = localDateKey(cursor)
  if (!(myDays.has(todayKey) && partnerDays.has(todayKey))) {
    cursor.setDate(cursor.getDate() - 1)
  }
  while (true) {
    const k = localDateKey(cursor)
    if (myDays.has(k) && partnerDays.has(k)) {
      streak += 1
      cursor.setDate(cursor.getDate() - 1)
    } else {
      break
    }
  }
  return streak
}
